import { useEffect, useRef, useState, useTransition, type RefObject } from "react"

const MAX = 80
const THRESH = 40

export const usePullToRefresh = (
  ref: RefObject<HTMLElement | null>,
  onRefresh: () => void,
  useWindowScroll = false,
) => {
  const [pullDistance, setPullDistance] = useState(0)
  const [isPending, startTransition] = useTransition()
  const s = useRef({ startY: 0, tracking: false, dist: 0 })
  const cb = useRef(onRefresh)
  cb.current = onRefresh

  useEffect(() => {
    if (!isPending) setPullDistance(0)
  }, [isPending])

  useEffect(() => {
    const target: EventTarget | null = useWindowScroll ? window : ref.current
    if (!target) return
    const getScrollTop = () => useWindowScroll ? window.scrollY : (ref.current?.scrollTop ?? 0)

    const onStart = (e: TouchEvent) => {
      if (getScrollTop() > 0 || isPending) return
      s.current.startY = e.touches[0].clientY
      s.current.dist = 0
      s.current.tracking = true
    }
    const onMove = (e: TouchEvent) => {
      if (!s.current.tracking) return
      s.current.dist = Math.min(MAX, Math.max(0, (e.touches[0].clientY - s.current.startY) * 0.5))
      setPullDistance(s.current.dist)
    }
    const onEnd = () => {
      if (!s.current.tracking) return
      s.current.tracking = false
      if (s.current.dist < THRESH) return setPullDistance(0)
      setPullDistance(THRESH)
      startTransition(() => cb.current())
    }
    target.addEventListener("touchstart", onStart as EventListener, { passive: true })
    target.addEventListener("touchmove", onMove as EventListener, { passive: true })
    target.addEventListener("touchend", onEnd as EventListener, { passive: true })
    return () => {
      target.removeEventListener("touchstart", onStart as EventListener)
      target.removeEventListener("touchmove", onMove as EventListener)
      target.removeEventListener("touchend", onEnd as EventListener)
    }
  }, [ref, isPending, startTransition, useWindowScroll])

  return { pullDistance, isRefreshing: isPending }
}
